/**
 * File System Access API + IndexedDB para autorizar e ler pastas de id_scgp.
 *
 * Estrutura esperada (única):
 *   PASTACONVENCAOCOLETIVA/
 *     {id_scgp}/
 *       *.zip
 */

const IDB_NAME = 'cct-buscar-dados-fs'
const IDB_STORE = 'handles'
/** Chave nova para invalidar autorização antiga de pasta incorreta. */
const IDB_KEY = 'root-directory-v2'

const PASTA_BASE_NAME = 'PASTACONVENCAOCOLETIVA'

type FileSystemPermissionMode = 'read' | 'readwrite'

interface FileSystemHandlePermissionDescriptor {
  mode?: FileSystemPermissionMode
}

interface FileSystemHandleWithPermission {
  queryPermission?: (descriptor?: FileSystemHandlePermissionDescriptor) => Promise<PermissionState>
  requestPermission?: (descriptor?: FileSystemHandlePermissionDescriptor) => Promise<PermissionState>
}

export interface CctBuscarDadosZipFile {
  name: string
  file: File
}

export interface CctBuscarDadosPastaLookupResult {
  zips: CctBuscarDadosZipFile[]
  pastaEncontrada: boolean
  rootName: string
  subpastasVisiveis: string[]
  caminhoResolvido: string | null
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, 1)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Falha ao abrir IndexedDB'))
  })
}

async function idbGet(): Promise<FileSystemDirectoryHandle | null> {
  const db = await openIdb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly')
    const store = tx.objectStore(IDB_STORE)
    const req = store.get(IDB_KEY)
    req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle) ?? null)
    req.onerror = () => reject(req.error ?? new Error('Falha ao ler handle da pasta'))
  })
}

async function idbSet(handle: FileSystemDirectoryHandle): Promise<void> {
  const db = await openIdb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite')
    const store = tx.objectStore(IDB_STORE)
    const req = store.put(handle, IDB_KEY)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error ?? new Error('Falha ao gravar handle da pasta'))
  })
}

async function idbClear(): Promise<void> {
  const db = await openIdb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite')
    const store = tx.objectStore(IDB_STORE)
    // Limpa chave atual e a antiga, se existir
    store.delete(IDB_KEY)
    store.delete('root-directory')
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('Falha ao limpar handle da pasta'))
  })
}

export function isFileSystemAccessSupported(): boolean {
  return typeof window !== 'undefined' && typeof (window as Window & {
    showDirectoryPicker?: unknown
  }).showDirectoryPicker === 'function'
}

async function ensurePermission(
  handle: FileSystemDirectoryHandle,
  mode: FileSystemPermissionMode = 'read',
): Promise<boolean> {
  const h = handle as FileSystemDirectoryHandle & FileSystemHandleWithPermission
  if (typeof h.queryPermission === 'function') {
    const current = await h.queryPermission({ mode })
    if (current === 'granted') return true
  }
  if (typeof h.requestPermission === 'function') {
    const next = await h.requestPermission({ mode })
    return next === 'granted'
  }
  return true
}

function normalizeFolderName(name: string): string {
  return name.replace(/\u00a0/g, ' ').trim().toLowerCase()
}

function namesMatch(a: string, b: string): boolean {
  return normalizeFolderName(a) === normalizeFolderName(b)
}

function isNumericIdFolder(name: string): boolean {
  return /^\d+$/.test(name.replace(/\u00a0/g, ' ').trim())
}

async function* iterateEntries(
  dir: FileSystemDirectoryHandle,
): AsyncGenerator<FileSystemHandle, void, unknown> {
  const anyDir = dir as FileSystemDirectoryHandle & {
    values?: () => AsyncIterableIterator<FileSystemHandle>
    entries?: () => AsyncIterableIterator<[string, FileSystemHandle]>
  }

  if (typeof anyDir.values === 'function') {
    for await (const entry of anyDir.values()) {
      yield entry
    }
    return
  }

  if (typeof anyDir.entries === 'function') {
    for await (const [, entry] of anyDir.entries()) {
      yield entry
    }
  }
}

async function listDirectoryNames(dir: FileSystemDirectoryHandle): Promise<string[]> {
  const directories: string[] = []
  try {
    for await (const entry of iterateEntries(dir)) {
      if (entry.kind === 'directory') directories.push(entry.name)
    }
  } catch {
    // rede/UNC às vezes falha ao enumerar
  }
  directories.sort((a, b) => a.localeCompare(b, 'pt-BR'))
  return directories
}

async function resolveChildDirectory(
  parent: FileSystemDirectoryHandle,
  wantedName: string,
): Promise<FileSystemDirectoryHandle | null> {
  try {
    return await parent.getDirectoryHandle(wantedName)
  } catch {
    // continua com varredura — comum em UNC
  }

  try {
    for await (const entry of iterateEntries(parent)) {
      if (entry.kind !== 'directory') continue
      if (namesMatch(entry.name, wantedName)) {
        return entry as FileSystemDirectoryHandle
      }
    }
  } catch {
    return null
  }

  return null
}

async function collectZips(dir: FileSystemDirectoryHandle): Promise<CctBuscarDadosZipFile[]> {
  const zips: CctBuscarDadosZipFile[] = []
  for await (const entry of iterateEntries(dir)) {
    if (entry.kind !== 'file') continue
    const name = entry.name
    if (!name.toLowerCase().endsWith('.zip')) continue
    const fileHandle = entry as FileSystemFileHandle
    const file = await fileHandle.getFile()
    zips.push({ name, file })
  }
  zips.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  return zips
}

/**
 * A pasta autorizada deve ser PASTACONVENCAOCOLETIVA (subpastas = id SCGP)
 * ou a própria pasta de um id (só zips).
 */
export async function assertValidCctRootDirectory(
  handle: FileSystemDirectoryHandle,
): Promise<void> {
  const directories = await listDirectoryNames(handle)
  const isBase = namesMatch(handle.name, PASTA_BASE_NAME)
  const hasIdFolders = directories.some(isNumericIdFolder)
  const isSingleId = isNumericIdFolder(handle.name)

  if (isBase || hasIdFolders || isSingleId) return

  throw new Error(
    `Pasta selecionada (“${handle.name}”) não é ${PASTA_BASE_NAME}. ` +
      `No diálogo, abra \\\\10.60.60.103\\Arquivos SCGP\\Parametros\\ e selecione ${PASTA_BASE_NAME} ` +
      '(pasta que contém subpastas com o número do id SCGP, ex.: 23742).',
  )
}

export async function authorizeRootDirectory(): Promise<FileSystemDirectoryHandle> {
  if (!isFileSystemAccessSupported()) {
    throw new Error(
      'Este navegador não permite autorizar pastas locais. Use Chrome ou Edge.',
    )
  }

  const picker = (window as unknown as {
    showDirectoryPicker: (options?: { mode?: string }) => Promise<FileSystemDirectoryHandle>
  }).showDirectoryPicker

  const handle = await picker({ mode: 'read' })
  await assertValidCctRootDirectory(handle)
  await idbSet(handle)
  return handle
}

export async function getAuthorizedRootDirectory(): Promise<FileSystemDirectoryHandle | null> {
  if (!isFileSystemAccessSupported()) return null
  try {
    const handle = await idbGet()
    if (!handle) return null
    const ok = await ensurePermission(handle, 'read')
    if (!ok) return null
    return handle
  } catch {
    return null
  }
}

export async function clearAuthorizedRootDirectory(): Promise<void> {
  await idbClear()
}

/**
 * Localiza PASTACONVENCAOCOLETIVA/{id_scgp}/*.zip a partir da pasta autorizada.
 */
export async function listZipFilesForIdScgp(
  root: FileSystemDirectoryHandle,
  idScgp: number,
): Promise<CctBuscarDadosPastaLookupResult> {
  const folderName = String(idScgp)
  const rootName = root.name
  const empty = (
    extras?: Partial<CctBuscarDadosPastaLookupResult>,
  ): CctBuscarDadosPastaLookupResult => ({
    zips: [],
    pastaEncontrada: false,
    rootName,
    subpastasVisiveis: [],
    caminhoResolvido: null,
    ...extras,
  })

  // Autorizou direto a pasta do id
  if (namesMatch(rootName, folderName) || (isNumericIdFolder(rootName) && namesMatch(rootName, folderName))) {
    const zips = await collectZips(root)
    return {
      zips,
      pastaEncontrada: true,
      rootName,
      subpastasVisiveis: [],
      caminhoResolvido: folderName,
    }
  }

  const rootDirs = await listDirectoryNames(root)

  // Caso normal: root = PASTACONVENCAOCOLETIVA → subpasta {id}
  const direct = await resolveChildDirectory(root, folderName)
  if (direct) {
    const zips = await collectZips(direct)
    return {
      zips,
      pastaEncontrada: true,
      rootName,
      subpastasVisiveis: rootDirs,
      caminhoResolvido: folderName,
    }
  }

  // Se autorizou Parametros (pai), entra em PASTACONVENCAOCOLETIVA/{id}
  if (!namesMatch(rootName, PASTA_BASE_NAME)) {
    const baseDir = await resolveChildDirectory(root, PASTA_BASE_NAME)
    if (baseDir) {
      const baseDirs = await listDirectoryNames(baseDir)
      const idDir = await resolveChildDirectory(baseDir, folderName)
      if (idDir) {
        const zips = await collectZips(idDir)
        return {
          zips,
          pastaEncontrada: true,
          rootName,
          subpastasVisiveis: baseDirs,
          caminhoResolvido: `${PASTA_BASE_NAME}/${folderName}`,
        }
      }
      return empty({ subpastasVisiveis: baseDirs })
    }
  }

  return empty({
    subpastasVisiveis: rootDirs.slice(0, 40),
  })
}
